import { type IdentifierRecord } from '@myplayer/schema';
import { IconAlertCircle, IconChevronLeft, IconQrCode, IconRefresh } from '@myplayer/ui';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from './auth-context.js';
import { Badge, Button, Select } from './components/ui/primitives.js';
import {
  analyzeQrCodeFrame,
  evaluateScannedQrCheck,
  type LiveLinkResolverProbe,
  type QrCheckEvaluation,
} from './qr-check-helper.js';
import { QrCheckResultsCard } from './qr-check-results-card.js';
import { resolveEffectiveHost, useWorkspaceIdentifiers } from './use-workspace-identifiers.js';

export function formatPlainCameraError(err: unknown): string {
  const name = err instanceof Error ? err.name : '';
  const message = err instanceof Error ? err.message.trim() : '';
  if (
    name === 'NotFoundError' ||
    name === 'DevicesNotFoundError' ||
    /requested device not found|device not found|object can not be found|no.*camera|overconstrained/i.test(
      message,
    )
  ) {
    return 'No camera was found on this device. Connect a camera or open this page on a phone or tablet with a camera.';
  }
  if (
    name === 'NotAllowedError' ||
    name === 'PermissionDeniedError' ||
    /permission denied|not allowed/i.test(message)
  ) {
    return 'Camera access was blocked. Allow camera access in your browser settings and try again.';
  }
  if (
    name === 'NotReadableError' ||
    name === 'TrackStartError' ||
    /could not start video source|in use/i.test(message)
  ) {
    return 'The camera is already in use by another app or tab. Close other camera apps and try again.';
  }
  return message.length > 0
    ? message
    : 'Could not open the camera. Check camera permissions and try again.';
}

export interface QrCheckFrameSource {
  readonly width: number;
  readonly height: number;
  readonly rgba: Uint8ClampedArray;
}

export interface QrCheckPageProps {
  readonly initialRecords?: readonly IdentifierRecord[];
  readonly initialSelectedIdentifierKey?: string | null;
  readonly resolverHost?: string;
  readonly autoStartCamera?: boolean;
  readonly initialFrame?: QrCheckFrameSource | null;
  readonly probeLiveResolution?: LiveLinkResolverProbe;
}

export function QrCheckPage({
  initialRecords,
  initialSelectedIdentifierKey,
  resolverHost,
  autoStartCamera = false,
  initialFrame = null,
  probeLiveResolution,
}: QrCheckPageProps = {}): ReactElement {
  const { session } = useAuth();
  const [searchParams] = useSearchParams();
  const queryProductKey = searchParams.get('product');
  const { records } = useWorkspaceIdentifiers(
    session,
    initialRecords,
    initialSelectedIdentifierKey ?? queryProductKey,
  );
  const effectiveResolverHost = resolveEffectiveHost(resolverHost);
  const [selectedProductKey, setSelectedProductKey] = useState<string>(
    initialSelectedIdentifierKey ?? queryProductKey ?? 'AUTO',
  );
  const [cameraActive, setCameraActive] = useState<boolean>(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [lastFrame, setLastFrame] = useState<QrCheckFrameSource | null>(initialFrame);
  const [evaluation, setEvaluation] = useState<QrCheckEvaluation | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback((): void => {
    if (streamRef.current !== null) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    if (videoRef.current !== null) {
      try {
        videoRef.current.srcObject = null;
      } catch {
        // jsdom fallback
      }
    }
    setCameraActive(false);
  }, []);

  const startCamera = useCallback(async (): Promise<void> => {
    setCameraError(null);
    if (typeof navigator?.mediaDevices?.getUserMedia !== 'function') {
      setCameraError(
        'Camera access is not available on this device or browser. Open this screen on a phone or laptop with a camera over HTTPS.',
      );
      setCameraActive(false);
      return;
    }
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 640 },
          height: { ideal: 640 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current !== null) {
        try {
          videoRef.current.srcObject = stream;
        } catch {
          // jsdom fallback
        }
        if (
          !/jsdom/i.test(navigator.userAgent ?? '') &&
          typeof videoRef.current.play === 'function'
        ) {
          await Promise.resolve(videoRef.current.play()).catch(() => undefined);
        }
      }
      setCameraActive(true);
    } catch (err) {
      setCameraError(formatPlainCameraError(err));
      setCameraActive(false);
    }
  }, [stopCamera]);

  const runFrameCheck = useCallback(
    async (frame: QrCheckFrameSource, productKeyOverride?: string): Promise<void> => {
      const nextEval = await evaluateScannedQrCheck({
        frameAnalysis: analyzeQrCodeFrame(frame.rgba, frame.width, frame.height),
        records,
        selectedIdentifierKey: productKeyOverride ?? selectedProductKey,
        effectiveResolverHost,
        ...(probeLiveResolution !== undefined ? { probeLiveResolution } : {}),
      });
      setEvaluation(nextEval);
    },
    [records, selectedProductKey, effectiveResolverHost, probeLiveResolution],
  );

  const captureVideoFrame = useCallback((): void => {
    const videoEl = videoRef.current;
    const canvasEl = canvasRef.current;
    if (!videoEl || !canvasEl) return;
    const w = videoEl.videoWidth > 0 ? videoEl.videoWidth : 320;
    const h = videoEl.videoHeight > 0 ? videoEl.videoHeight : 320;
    canvasEl.width = w;
    canvasEl.height = h;
    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(videoEl, 0, 0, w, h);
    const img = ctx.getImageData(0, 0, w, h);
    const frame: QrCheckFrameSource = {
      width: img.width > 0 ? img.width : w,
      height: img.height > 0 ? img.height : h,
      rgba: img.data,
    };
    setLastFrame(frame);
    void runFrameCheck(frame);
  }, [runFrameCheck]);

  useEffect(() => {
    if (autoStartCamera) void startCamera();
    return () => stopCamera();
  }, [autoStartCamera, startCamera, stopCamera]);

  useEffect(() => {
    if (lastFrame !== null) void runFrameCheck(lastFrame, selectedProductKey);
  }, [lastFrame, selectedProductKey, runFrameCheck]);

  useEffect(() => {
    if (!cameraActive) return;
    const timer = window.setInterval(captureVideoFrame, 500);
    return () => window.clearInterval(timer);
  }, [cameraActive, captureVideoFrame]);

  return (
    <div
      data-testid="qr-check-page"
      className="fixed inset-0 flex h-dvh w-dvw flex-col overflow-hidden bg-page text-fg landscape:flex-row"
    >
      {/* 50% Scanner Pane (Top in portrait, Left in landscape) */}
      <section
        aria-label="QR camera scanner"
        data-testid="qr-check-scanner-pane"
        className="relative flex h-1/2 w-full shrink-0 flex-col overflow-hidden border-b border-border bg-surface-subtle landscape:h-full landscape:w-1/2 landscape:border-b-0 landscape:border-r"
      >
        <div
          data-testid="qr-check-viewfinder"
          className="relative flex h-full w-full flex-1 flex-col items-center justify-center overflow-hidden"
        >
          <video
            ref={videoRef}
            data-testid="qr-check-video"
            playsInline
            muted
            className={`h-full w-full object-cover ${cameraActive ? 'block' : 'hidden'}`}
          />
          <canvas ref={canvasRef} data-testid="qr-check-canvas" className="hidden" />

          <div className="pointer-events-none absolute left-3 right-3 top-3 z-10 flex items-center justify-between gap-2">
            <div className="pointer-events-auto flex items-center gap-2">
              <Link
                to={
                  selectedProductKey && selectedProductKey !== 'AUTO'
                    ? `/products/${encodeURIComponent(selectedProductKey)}`
                    : '/products'
                }
                data-testid="qr-check-back-link"
                className="inline-flex h-[26px] items-center gap-1 rounded-pill border border-border bg-surface px-2.5 text-[12px] font-semibold text-fg transition-colors hover:border-border-medium hover:bg-surface-subtle"
              >
                <IconChevronLeft className="h-3.5 w-3.5" />
                <span>Back to Admin</span>
              </Link>
              <Badge
                variant={cameraActive ? 'valid' : 'secondary'}
                data-testid="qr-check-camera-status-badge"
              >
                {cameraActive ? 'Camera live' : 'Camera paused'}
              </Badge>
            </div>
            {cameraActive && (
              <div className="pointer-events-auto flex items-center gap-1.5">
                <Button
                  type="button"
                  variant="primary"
                  size="xs"
                  data-testid="qr-check-scan-frame-button"
                  onClick={captureVideoFrame}
                >
                  <IconRefresh />
                  <span>Scan frame</span>
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="xs"
                  data-testid="qr-check-stop-camera-button"
                  onClick={stopCamera}
                >
                  Pause
                </Button>
              </div>
            )}
          </div>

          {!cameraActive ? (
            <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-border bg-surface text-brand">
                <IconQrCode />
              </div>
              <p className="max-w-xs text-[13px] font-medium text-fg">
                Open your camera and centre the printed QR code inside the frame.
              </p>
              <Button
                type="button"
                variant="primary"
                size="default"
                data-testid="qr-check-start-camera-button"
                onClick={() => void startCamera()}
              >
                <IconQrCode />
                <span>Open camera</span>
              </Button>
            </div>
          ) : (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-6 rounded-card border-2 border-dashed border-brand sm:inset-10"
            />
          )}

          {cameraError !== null && (
            <div
              role="alert"
              data-testid="qr-check-camera-error"
              className="absolute bottom-3 left-3 right-3 z-10 flex items-start gap-2 rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
            >
              <IconAlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{cameraError}</span>
            </div>
          )}
        </div>
      </section>

      {/* 50% Info Pane (Bottom in portrait, Right in landscape) */}
      <section
        aria-label="QR check results and product info"
        data-testid="qr-check-info-pane"
        className="flex h-1/2 w-full shrink-0 flex-col gap-3 overflow-y-auto bg-page p-4 sm:p-6 landscape:h-full landscape:w-1/2"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-[15px] font-semibold text-fg">QR Code Check</h1>
          <Link
            to={
              selectedProductKey && selectedProductKey !== 'AUTO'
                ? `/products/${encodeURIComponent(selectedProductKey)}`
                : '/products'
            }
            data-testid="qr-check-info-back-link"
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand hover:underline"
          >
            <IconChevronLeft className="h-3.5 w-3.5" />
            <span>Back to Products</span>
          </Link>
        </div>
        <div className="space-y-1">
          <label
            htmlFor="qr-check-product-select"
            className="block text-[12px] font-semibold text-fg"
          >
            Product to check against
          </label>
          <Select
            id="qr-check-product-select"
            data-testid="qr-check-product-select"
            value={selectedProductKey}
            onChange={(e) => setSelectedProductKey(e.target.value)}
          >
            <option value="AUTO">Auto-match from workspace products</option>
            {records.map((rec) => (
              <option key={rec.identifierKey} value={rec.identifierKey}>
                {rec.draft?.title ?? rec.title ?? rec.primaryKey} ({rec.primaryKey})
              </option>
            ))}
          </Select>
        </div>

        <QrCheckResultsCard evaluation={evaluation} />
      </section>
    </div>
  );
}
